/**
 * NextJS-specific session utilities
 * Complement cross-domain functionality
 */

import { NextRequest } from "next/server";
import { Session } from "@supabase/supabase-js";
import { getDomainInfo, isProductionDomain } from "@/lib/utils/domainDetection";
import { createSessionCookieManager } from "@/lib/utils/cookieManager";
import { createCrossOriginMessaging } from "@/lib/utils/crossOriginMessaging";
import { createCrossDomainStorage } from "@/lib/utils/crossDomainStorage";

export interface SessionValidationResult {
  isValid: boolean;
  session: Session | null;
  error?: string;
  source: "cookie" | "localStorage" | "server";
}

export interface CrossDomainSessionStatus {
  hasSession: boolean;
  isValid: boolean;
  source: string;
  lastSync: number | null;
  conflicts: string[];
  debug: Record<string, any>;
}

export interface SessionConflict {
  type: "expired" | "invalid" | "mismatch" | "missing";
  message: string;
  resolution: "use_local" | "use_remote" | "clear_all" | "manual";
}

/**
 * Session utilities for cross-domain management
 */
export class SessionUtils {
  private cookieManager: ReturnType<typeof createSessionCookieManager>;
  private messaging: ReturnType<typeof createCrossOriginMessaging>;
  private storage: ReturnType<typeof createCrossDomainStorage>;
  private isServer: boolean;

  constructor() {
    const domainInfo = getDomainInfo();
    this.cookieManager = createSessionCookieManager("SUPABASE_SESSION", {
      enableDebug: process.env.NODE_ENV === "development",
      defaultDomain: domainInfo.cookieDomain,
    });
    this.messaging = createCrossOriginMessaging({
      enableDebug: process.env.NODE_ENV === "development",
    });
    this.storage = createCrossDomainStorage({
      enableDebug: process.env.NODE_ENV === "development",
    });
    this.isServer = typeof window === "undefined";
  }

  /**
   * Validate session from server-side request
   */
  validateSessionServer(request: NextRequest): SessionValidationResult {
    try {
      const sessionData = this.cookieManager.getSessionServer(request);

      if (!sessionData) {
        return {
          isValid: false,
          session: null,
          source: "cookie",
          error: "No session found in cookies",
        };
      }

      // Validate session structure
      if (!this.isValidSessionStructure(sessionData)) {
        return {
          isValid: false,
          session: null,
          source: "cookie",
          error: "Invalid session structure",
        };
      }

      // Check expiration
      if (this.isSessionExpired(sessionData)) {
        return {
          isValid: false,
          session: null,
          source: "cookie",
          error: "Session expired",
        };
      }

      return {
        isValid: true,
        session: sessionData,
        source: "cookie",
      };
    } catch (error) {
      return {
        isValid: false,
        session: null,
        source: "cookie",
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Validate session from client-side storage
   */
  validateSessionClient(): SessionValidationResult {
    if (this.isServer) {
      return {
        isValid: false,
        session: null,
        source: "localStorage",
        error: "Cannot validate client session on server",
      };
    }

    try {
      const sessionData = this.storage.getSessionData();

      if (!sessionData) {
        return {
          isValid: false,
          session: null,
          source: "localStorage",
          error: "No session found in storage",
        };
      }

      // Validate session structure
      if (!this.isValidSessionStructure(sessionData)) {
        return {
          isValid: false,
          session: null,
          source: "localStorage",
          error: "Invalid session structure",
        };
      }

      // Check expiration
      if (this.isSessionExpired(sessionData)) {
        return {
          isValid: false,
          session: null,
          source: "localStorage",
          error: "Session expired",
        };
      }

      return {
        isValid: true,
        session: sessionData,
        source: "localStorage",
      };
    } catch (error) {
      return {
        isValid: false,
        session: null,
        source: "localStorage",
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Get cross-domain session status
   */
  getCrossDomainSessionStatus(): CrossDomainSessionStatus {
    const domainInfo = getDomainInfo();
    const hasSession = this.storage.getSessionData() !== null;
    const isValid = this.storage.isSessionValid();

    return {
      hasSession,
      isValid,
      source: domainInfo.isProduction ? "cookie" : "hybrid",
      lastSync: this.getLastSyncTime(),
      conflicts: this.detectSessionConflicts(),
      debug: {
        domain: domainInfo,
        storage: {
          hasLocalStorage:
            typeof window !== "undefined" && !!window.localStorage,
          hasCookies: typeof document !== "undefined" && !!document.cookie,
          storageType: domainInfo.isProduction ? "cookie" : "hybrid",
        },
        messaging: this.messaging.getConnectionStatus(),
      },
    };
  }

  /**
   * Detect session conflicts between different storage methods
   */
  detectSessionConflicts(): string[] {
    const conflicts: string[] = [];

    if (this.isServer) return conflicts;

    try {
      const cookieSession = this.cookieManager.getSession();
      const storageSession = this.storage.getSessionData();

      // Check for session existence conflicts
      if (cookieSession && !storageSession) {
        conflicts.push("Session exists in cookies but not in localStorage");
      } else if (!cookieSession && storageSession) {
        conflicts.push("Session exists in localStorage but not in cookies");
      }

      // Check for session data conflicts
      if (cookieSession && storageSession) {
        const cookieUserId = cookieSession.user?.id;
        const storageUserId = storageSession.user?.id;

        if (cookieUserId !== storageUserId) {
          conflicts.push(
            "Different user IDs in cookie vs localStorage session"
          );
        }

        const cookieExpiry = cookieSession.expires_at;
        const storageExpiry = storageSession.expires_at;

        if (Math.abs(cookieExpiry - storageExpiry) > 60) {
          // 1 minute tolerance
          conflicts.push(
            "Different expiration times in cookie vs localStorage session"
          );
        }
      }
    } catch (error) {
      conflicts.push(
        `Error detecting conflicts: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }

    return conflicts;
  }

  /**
   * Resolve session conflicts
   */
  resolveSessionConflicts(
    strategy:
      | "use_newest"
      | "use_cookie"
      | "use_storage"
      | "clear_all" = "use_newest"
  ): boolean {
    if (this.isServer) return false;

    try {
      const cookieSession = this.cookieManager.getSession();
      const storageSession = this.storage.getSessionData();

      switch (strategy) {
        case "use_newest":
          const cookieTime = cookieSession?.expires_at || 0;
          const storageTime = storageSession?.expires_at || 0;

          if (cookieTime > storageTime) {
            this.storage.setSessionData(cookieSession);
          } else if (storageTime > cookieTime) {
            this.cookieManager.setSession(storageSession);
          }
          break;

        case "use_cookie":
          if (cookieSession) {
            this.storage.setSessionData(cookieSession);
          } else {
            this.storage.clearSession();
          }
          break;

        case "use_storage":
          if (storageSession) {
            this.cookieManager.setSession(storageSession);
          } else {
            this.cookieManager.clearSession();
          }
          break;

        case "clear_all":
          this.storage.clearSession();
          this.cookieManager.clearSession();
          break;
      }

      return true;
    } catch (error) {
      console.error("[SessionUtils] Error resolving conflicts:", error);
      return false;
    }
  }

  /**
   * Sync session across domains
   */
  async syncSessionAcrossDomains(): Promise<boolean> {
    if (this.isServer) return false;

    try {
      const sessionData = this.storage.getSessionData();

      if (sessionData) {
        // Broadcast current session to other domains
        this.messaging.broadcastSessionUpdate(
          "SESSION_SYNC",
          JSON.stringify(sessionData)
        );
      } else {
        // Request session from other domains
        this.messaging.requestSessionSync();
      }

      return true;
    } catch (error) {
      console.error("[SessionUtils] Error syncing session:", error);
      return false;
    }
  }

  /**
   * Clear all session data
   */
  clearAllSessionData(): void {
    if (this.isServer) return;

    this.storage.clearSession();
    this.cookieManager.clearSession();

    // Broadcast logout to other domains
    this.messaging.broadcastSessionUpdate("SESSION_LOGOUT", null);
  }

  /**
   * Check if session structure is valid
   */
  private isValidSessionStructure(sessionData: any): boolean {
    return (
      sessionData &&
      typeof sessionData === "object" &&
      typeof sessionData.access_token === "string" &&
      typeof sessionData.refresh_token === "string" &&
      typeof sessionData.expires_at === "number" &&
      sessionData.user &&
      typeof sessionData.user.id === "string"
    );
  }

  /**
   * Check if session is expired
   */
  private isSessionExpired(sessionData: any): boolean {
    if (!sessionData || typeof sessionData.expires_at !== "number") {
      return true;
    }

    const now = Math.floor(Date.now() / 1000);
    return sessionData.expires_at <= now;
  }

  /**
   * Get last sync time
   */
  private getLastSyncTime(): number | null {
    if (this.isServer) return null;

    try {
      const lastSync = localStorage.getItem("last_session_sync");
      return lastSync ? parseInt(lastSync, 10) : null;
    } catch {
      return null;
    }
  }

  /**
   * Set last sync time
   */
  setLastSyncTime(): void {
    if (this.isServer) return;

    try {
      localStorage.setItem("last_session_sync", Date.now().toString());
    } catch (error) {
      console.error("[SessionUtils] Error setting last sync time:", error);
    }
  }

  /**
   * Get session metadata
   */
  getSessionMetadata(): Record<string, any> {
    const sessionData = this.storage.getSessionData();

    if (!sessionData) {
      return {
        hasSession: false,
        isValid: false,
        userId: null,
        email: null,
        expiresAt: null,
        tokenType: null,
      };
    }

    return {
      hasSession: true,
      isValid: this.storage.isSessionValid(),
      userId: sessionData.user?.id,
      email: sessionData.user?.email,
      expiresAt: sessionData.expires_at,
      tokenType: sessionData.token_type,
      createdAt: sessionData.user?.created_at,
      lastSignIn: sessionData.user?.last_sign_in_at,
    };
  }

  /**
   * Test cross-domain connectivity
   */
  async testCrossDomainConnectivity(): Promise<{ [origin: string]: boolean }> {
    if (this.isServer) return {};

    return this.messaging.testConnectivity();
  }

  /**
   * Get debug information
   */
  getDebugInfo(): Record<string, any> {
    const domainInfo = getDomainInfo();
    const sessionStatus = this.getCrossDomainSessionStatus();
    const metadata = this.getSessionMetadata();

    return {
      timestamp: new Date().toISOString(),
      domain: domainInfo,
      session: {
        status: sessionStatus,
        metadata: metadata,
      },
      storage: {
        localStorage:
          typeof window !== "undefined"
            ? {
                available: !!window.localStorage,
                keys: window.localStorage
                  ? Object.keys(window.localStorage)
                  : [],
              }
            : "server",
        cookies: typeof document !== "undefined" ? document.cookie : "server",
      },
      messaging: this.messaging.getConnectionStatus(),
    };
  }
}

/**
 * Create session utils instance
 */
export function createSessionUtils(): SessionUtils {
  return new SessionUtils();
}

/**
 * Default session utils instance
 */
export const sessionUtils = createSessionUtils();

/**
 * Utility functions for common session operations
 */

/**
 * Check if user is authenticated (server-side)
 */
export async function isAuthenticatedServer(
  request: NextRequest
): Promise<boolean> {
  const result = sessionUtils.validateSessionServer(request);
  return result.isValid;
}

/**
 * Check if user is authenticated (client-side)
 */
export function isAuthenticatedClient(): boolean {
  const result = sessionUtils.validateSessionClient();
  return result.isValid;
}

/**
 * Get current session (server-side)
 */
export async function getCurrentSessionServer(
  request: NextRequest
): Promise<Session | null> {
  const result = sessionUtils.validateSessionServer(request);
  return result.session;
}

/**
 * Get current session (client-side)
 */
export function getCurrentSessionClient(): Session | null {
  const result = sessionUtils.validateSessionClient();
  return result.session;
}

/**
 * Get current user ID (server-side)
 */
export async function getCurrentUserIdServer(
  request: NextRequest
): Promise<string | null> {
  const session = await getCurrentSessionServer(request);
  return session?.user?.id || null;
}

/**
 * Get current user ID (client-side)
 */
export function getCurrentUserIdClient(): string | null {
  const session = getCurrentSessionClient();
  return session?.user?.id || null;
}

/**
 * Require authentication (server-side)
 */
export async function requireAuthServer(
  request: NextRequest
): Promise<{ user: Session; error?: never } | { user?: never; error: string }> {
  const session = await getCurrentSessionServer(request);

  if (!session) {
    return { error: "Authentication required" };
  }

  return { user: session };
}

/**
 * Require authentication (client-side)
 */
export function requireAuthClient():
  | { user: Session; error?: never }
  | { user?: never; error: string } {
  const session = getCurrentSessionClient();

  if (!session) {
    return { error: "Authentication required" };
  }

  return { user: session };
}
